import { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonV2, DataGrid, DataGridColumn } from '@cimplico/components';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { CheckIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/20/solid';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { FirmNotApplicableDescription } from '../../../../types/firm';
import { useReplaceFirmNotApplicableDescriptions } from '../../../../queries/use-replace-firm-not-applicable-descriptions';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../../../../hooks/use-is-firm-role';
import { toastNotification } from '../../../../components/toast/Toast';
import { ROW_SIZES } from '../../../../constants';
import NewEntryRow from './NewEntryRow';
import SectionLabel from '../../../../components/section-label/SectionLabel';
import SettingsContainer from '../../../../components/settings-container/SettingsContainer';
import EmptyStateNew from '../../../../components/empty-state/EmptyStateNew';

const columns: DataGridColumn[] = [
    {
        alignment: 'center',
        width: '1fr',
    },
    {
        alignment: 'left',
        width: '0.1fr',
    },
];

type NotApplicableDescriptionFields = {
    descriptions: { id: string; value: string; sequence?: number }[];
};

type FirmNotApplicableDescriptionsProps = {
    notApplicableDescriptions: FirmNotApplicableDescription[];
};

function FirmNotApplicableDescriptions({ notApplicableDescriptions }: FirmNotApplicableDescriptionsProps): JSX.Element {
    const [isEditing, setIsEditing] = useState(false);

    const methods = useForm<NotApplicableDescriptionFields>({
        defaultValues: {
            descriptions:
                notApplicableDescriptions.length !== 0
                    ? notApplicableDescriptions.map((d) => {
                          return { id: d.id, value: d.description, sequence: d.sequence };
                      })
                    : [{ id: 'row-0', value: '' }],
        },
    });

    const activeSubscription = useIsActiveSubscription();
    const { isAdmin, isManager } = useIsFirmRole();
    const { append, remove } = useFieldArray({ control: methods.control, name: 'descriptions' });

    const roleCanCreate = isAdmin || isManager;

    const replaceNotApplicableDescriptionsQuery = useReplaceFirmNotApplicableDescriptions();

    const rowIdCounter = useRef<number>(notApplicableDescriptions.length !== 0 ? notApplicableDescriptions.length - 1 : 1);

    function addRow(): void {
        append({ id: `row-${rowIdCounter.current + 1}`, value: '' });
        rowIdCounter.current += 1;
    }

    function deleteRow(index: number): void {
        if (methods.getValues('descriptions').length === 1) {
            return;
        }
        remove(index);
    }

    async function handleSubmit(): Promise<void> {
        const values = methods.getValues();
        const validNotApplicableDescriptions = values.descriptions.filter((d) => d.value);
        const allNotApplicableDescriptions = validNotApplicableDescriptions.map((val) => {
            return { ...val, sequence: validNotApplicableDescriptions.map((d) => d.id).indexOf(val.id) + 1 };
        });

        // if no changes on not applicable descriptions
        if (
            notApplicableDescriptions.length === allNotApplicableDescriptions.length &&
            notApplicableDescriptions.every((val) => !val.id.includes('row-'))
        ) {
            const defaultValues = notApplicableDescriptions
                .sort((a, b) => (a.sequence > b.sequence ? 1 : -1))
                .map((val) => {
                    return { id: val.id, description: val.description, sequence: val.sequence };
                });
            const newValues = allNotApplicableDescriptions
                .sort((a, b) => (a.sequence > b.sequence ? 1 : -1))
                .map((val) => {
                    return { id: val.id, description: val.value, sequence: val.sequence };
                });

            if (JSON.stringify(defaultValues) === JSON.stringify(newValues)) {
                handleReset();
                return;
            }
        }

        // if no available descriptions
        if (allNotApplicableDescriptions.length === 0 && notApplicableDescriptions.length === 0) {
            handleReset();
            return;
        }

        replaceNotApplicableDescriptionsQuery.mutate(
            allNotApplicableDescriptions.map((val) => {
                return { description: val.value, sequence: val.sequence };
            }),
            {
                onSuccess: () => {
                    toastNotification('success', 'Firm not applicable descriptions updated!');
                    setIsEditing(false);
                },
                onError: () => {
                    toastNotification('error', 'Unable to update firm not applicable descriptions!');
                },
            }
        );
    }

    function handleReset(): void {
        if (notApplicableDescriptions.length === 0) {
            methods.setValue('descriptions', [{ id: 'row-0', value: '' }]);
        } else {
            methods.setValue(
                'descriptions',
                notApplicableDescriptions.map((d) => {
                    return { id: d.id, value: d.description, sequence: d.sequence };
                })
            );
        }
        rowIdCounter.current = notApplicableDescriptions.length !== 0 ? notApplicableDescriptions.length - 1 : 1;
        setIsEditing(false);
    }

    useEffect(() => {
        if (notApplicableDescriptions.length === 0) {
            methods.setValue('descriptions', [{ id: 'row-0', value: '' }]);
        } else {
            methods.setValue(
                'descriptions',
                notApplicableDescriptions.map((d) => {
                    return { id: d.id, value: d.description, sequence: d.sequence };
                })
            );
        }
    }, [notApplicableDescriptions]);

    function reorderLines(sortedIds: string[]): void {
        const prevValues = methods.getValues('descriptions');
        const sortedValues = [...prevValues].sort((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id));
        methods.setValue('descriptions', sortedValues);
    }

    return (
        <FormProvider {...methods}>
            <SettingsContainer isLastItem>
                <SectionLabel
                    title="Not applicable descriptions"
                    description="Create quick descriptions users can select when marking procedures or checklists as not applicable."
                />
                <section className="grid gap-y-8">
                    <div className="grid gap-y-2">
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Descriptions</label>
                        <div className="lg:w-fit">
                            <DataGrid
                                columns={columns}
                                rowSizes={ROW_SIZES}
                                totalRows={999}
                                isSelectable={false}
                                showRowBorder={false}
                                onMove={reorderLines}
                                disablePagination
                                isDraggable
                            >
                                {!isEditing && notApplicableDescriptions.length === 0 && (
                                    <div className="w-full lg:w-[500px]">
                                        <EmptyStateNew
                                            actionText="New Description"
                                            actionButtonType="secondary"
                                            helpLink="https://learn.cimplico.com/docs/firm-descriptions"
                                            helpText="Create new options that will appear in the select for a procedure/checklist item's not applicable description."
                                            title="Add your first firm not applicable description"
                                            titleSize="small"
                                            disableActionButton={!roleCanCreate}
                                            disableActionButtonHint={!roleCanCreate ? 'This requires an admin or manager role.' : undefined}
                                            onClick={(): void => setIsEditing(true)}
                                        />
                                    </div>
                                )}
                                {(isEditing || notApplicableDescriptions.length !== 0) &&
                                    methods
                                        .getValues('descriptions')
                                        .map((entry, index: number) => (
                                            <NewEntryRow
                                                id={entry.id}
                                                index={index}
                                                key={entry.id}
                                                onDelete={deleteRow}
                                                isEditing={isEditing}
                                                canRemove={methods.getValues('descriptions').length > 1}
                                            />
                                        ))}
                            </DataGrid>
                        </div>
                        {isEditing && (
                            <div className="pl-14">
                                <ButtonV2
                                    size="sm"
                                    type="secondary"
                                    label="Add description"
                                    leftIcon={<PlusCircleIcon />}
                                    onClick={addRow}
                                />
                            </div>
                        )}
                    </div>
                    {isEditing ? (
                        <div className="flex gap-x-4">
                            <ButtonV2 label="Save" size="sm" leftIcon={<CheckIcon />} onClick={handleSubmit} />
                            <ButtonV2 size="sm" type="hollow" label="Discard" leftIcon={<TrashIcon />} onClick={handleReset} />
                        </div>
                    ) : (
                        (isAdmin || isManager) &&
                        notApplicableDescriptions.length > 0 && (
                            <div>
                                <ButtonV2
                                    size="sm"
                                    type="secondary"
                                    label="Edit"
                                    leftIcon={<PencilSquareIcon />}
                                    onClick={(): void => setIsEditing(true)}
                                    isDisabled={!activeSubscription || !roleCanCreate}
                                    disabledHint={!roleCanCreate ? 'This requires an admin or manager role.' : undefined}
                                />
                            </div>
                        )
                    )}
                </section>
            </SettingsContainer>
        </FormProvider>
    );
}

export default FirmNotApplicableDescriptions;
